import type { AutoQaRatingCategory } from '@zendesk/zqa-services/autoqa'
import type {
  AdvancedScorecard_RootCauses as RootCauses,
  AdvancedScorecard_ScorecardRatingCategoryRating as CategoryRating,
} from '@zendesk/zqa-services/scorecards'
import { type Scale, type ScaleType, SCALE_VALUES } from '@/data/scales'
import type { Scorecard } from '@/modules/shared/TicketContent/types/scorecard'
import { type RootCause, RequireReasonVisibility, type AutoQaCategoryType } from '@/types/category'

/**
 * This utils module should house functional methods to better unify and
 * re-use the different ratings and grouping methods used in:
 *
 * Feedback.vue
 * ReviewSidebar.vue (TicketContent)
 * Review.vue (extension)
 */

// Golang services types
export interface Category {
  categoryDescription?: string
  categoryId: number
  categoryName: string
  critical: boolean
  reviewCritical: boolean
  groupCritical: boolean
  groupId?: number | string
  groupName?: string
  groupPosition?: number
  isArchived: boolean
  multipleRequireReasons: boolean
  position: number
  requireReason: boolean
  requireReasonVisibility: RequireReasonVisibility
  autoQaApplicabilities: string[]
  freeTextAllowed: boolean
  rootCauses: RootCause[]
  scale: ScaleType
  scorecards: Scorecard[]
  weight: number
  autoQaCategory?: AutoQaCategoryType
  autoQaCustomCategoryTemplateId?: string
  autoQaRatingCategory?: AutoQaRatingCategory
  advancedRootCauses?: RootCauses
  skippable: boolean
  isAdvancedScorecardCategory?: boolean
  scorecardId?: string
  categoryRatings: CategoryRating[]
  status?: string
}

export interface Rating extends Category {
  rating?: number
  cause?: string[]
  failCategory?: boolean
  scorecardId?: string
}

export interface CategoryGroup {
  id: number
  name: string
  position: number
  categories: Rating[]
}

export type GivenRatings = Record<string, Rating>

// This value is used to mark "N/A" ratings if the Account-level Scale settings allow it
export const RATING_NA = 42

export const populateWithNA = (givenRatings: GivenRatings): Rating[] =>
  Object.values(givenRatings).map((r) => {
    if (r.skippable) {
      return { ...r, rating: r.rating ?? RATING_NA }
    } else {
      return r
    }
  })

export const groupCategories = <T extends { id: number; name: string; position: number }>(
  categories: Rating[],
  groups: T[],
  keepEmptyGroups = false,
): CategoryGroup[] => {
  const ungroupedCategories = categories
    .filter((cat) => !cat.groupId)
    .sort((a, b) => (a.position || 0) - (b.position || 0))
  return [
    getUngroupedCategoryGroup(ungroupedCategories),
    ...groups
      .map((group) => ({
        ...group,
        categories: categories
          .filter((cat) => cat.groupId === group.id)
          .sort((a, b) => (a.position || 0) - (b.position || 0)),
      }))
      .filter((group) => keepEmptyGroups || group.categories.length),
  ]
}

export const isRated = (r?: { rating?: number }) => r && ![null, undefined, RATING_NA].includes(r.rating)

export const isValidComment = (comment: string) => {
  const content = comment.replace(/(<p[^>]+?>|<p>|<\/p>)/gim, '').replace(/&#39;/g, `'`)
  return !!content.trim().length
}

export const hasRootCauses = (cause: string[] | undefined) => !!cause?.length

export const isNegativeRating = (rating: number, scale: Scale[]) => rating < Math.floor((scale?.length ?? 0) / 2)

export const isNeutralRating = (rating: number, scale: Scale[]) => {
  if (scale.length % 2 !== 1) {
    // Array does not have a neutral rating (i.e. it's either a binary or 4-point scale)
    return false
  }

  const neutralRatingIndex = Math.floor(scale.length / 2)
  const neutralRating = scale[neutralRatingIndex].label - 1

  return neutralRating === rating
}

export const isRootCauseRequiredForRating = (rating: number, ratingCategory: Rating): boolean => {
  const { requireReason, requireReasonVisibility, scale, isAdvancedScorecardCategory } = ratingCategory

  if (!requireReason) return false
  if (isAdvancedScorecardCategory) return isReasonRequiredForRating(rating, ratingCategory.advancedRootCauses)

  // legacy category
  if (requireReasonVisibility === RequireReasonVisibility.All) return true

  if (requireReasonVisibility === RequireReasonVisibility.NegativeAndNeutral) {
    return isNegativeRating(rating, SCALE_VALUES[scale]) || isNeutralRating(rating, SCALE_VALUES[scale])
  }

  return isNegativeRating(rating, SCALE_VALUES[scale])
}

export const isReasonRequiredForRatingLegacy = (
  rating: number,
  scale: Scale[],
  requireReasonVisibility: RequireReasonVisibility,
): boolean => {
  switch (requireReasonVisibility) {
    case RequireReasonVisibility.All:
      return true
    case null:
    case RequireReasonVisibility.Negative: {
      return isNegativeRating(rating, scale!)
    }
    case RequireReasonVisibility.NegativeAndNeutral: {
      const negativeRating = isNegativeRating(rating, scale!)
      const neutralRating = isNeutralRating(rating, scale!)
      return negativeRating || neutralRating
    }
    default:
      return false
  }
}

const getRootCausesRating = (rating: number, rootCauses: RootCauses | undefined) => {
  if (rating === null) return undefined

  const rootCausesRating = rootCauses?.ratings.find((r) => r.rating === rating)
  if (!rootCausesRating) return undefined

  return rootCausesRating
}

export const getRootCausesForRating = (rating: number, rootCauses: RootCauses) => {
  return getRootCausesRating(rating, rootCauses)?.rootCauses || []
}

export const isReasonRequiredForRating = (rating: number, rootCauses: RootCauses | undefined): boolean => {
  if (!rootCauses) return false

  const rootCausesForRating = getRootCausesRating(rating, rootCauses)

  if (!rootCausesForRating) return false

  return rootCausesForRating.rootCauses.length > 0 || !!rootCausesForRating.freeTextAllowed
}

export const getUngroupedCategoryGroup = (categories: Category[]): CategoryGroup => ({
  id: 0,
  name: 'Ungrouped',
  position: 0,
  categories,
})

// if it's legacy, it will return the causes as they are
// if freeTextAllowed is true, it will return the causes as they are
// if freeTextAllowed is false, it will filter out any cause that is not in the category's advanced root causes
export const getValidRootCauses = (
  rating: number,
  causes: string[] | undefined,
  advancedRootCauses: RootCauses | undefined,
  freeTextAllowed = false,
  legacy = true,
): string[] => {
  if (!causes || causes.length === 0) return []
  if (legacy || freeTextAllowed) {
    return causes
  }

  const rootCausesForRating = getRootCausesRating(rating, advancedRootCauses)

  if (!rootCausesForRating) return []
  if (rootCausesForRating.freeTextAllowed) return causes

  const existingRootCauses = (rootCausesForRating.rootCauses || []).map((c) => c.name)
  return causes.filter((c: string) => existingRootCauses.includes(c))
}
